export type CancellationReason = {
  value: number;
  label: string;
  template?: string;
}

export const OTHER_CANCELLATION_REASON_VALUE = 6
export const HST_DISQUALIFIED_CANCELLATION_REASON_VALUE = 10

export type HstDisqualifyingPaperworkField =
  | 'congestiveHeartFailure'
  | 'hypoventilationSyndrome'
  | 'moderateToSeverePulmonaryDisease'
  | 'historyOfStroke'

export type HstDisqualifyingCondition = {
  paperworkField: HstDisqualifyingPaperworkField
  label: string
}

export type HstScreeningAnswers = Partial<Record<HstDisqualifyingPaperworkField, string>> & {
  strokeSymptoms?: string
}

export const isHstDisqualifyingAnswer = (
  answers: HstScreeningAnswers | undefined | null,
  field: HstDisqualifyingPaperworkField,
): boolean =>
  answers?.[field] === 'yes'
  && (field !== 'historyOfStroke' || answers.strokeSymptoms !== 'resolved')

export const hstDisqualifyingConditions: HstDisqualifyingCondition[] = [
  { paperworkField: 'congestiveHeartFailure', label: 'Congestive Heart Failure' },
  { paperworkField: 'hypoventilationSyndrome', label: 'Hypoventilation Syndrome' },
  {
    paperworkField: 'moderateToSeverePulmonaryDisease',
    label: 'Severe COPD',
  },
  {
    paperworkField: 'historyOfStroke',
    label: 'History of Stroke',
  },
]

export const cancellationReasons: CancellationReason[] = [
  {
    value: 1,
    label: 'Unable to Reach Patient',
    template:
      'We are canceling this order due to multiple unsuccessful attempts to contact the patient regarding scheduling. Please feel free to re-refer if the patient becomes reachable.',
  },
  {
    value: 2,
    label: 'Patient Refused Testing',
    template:
      'We are canceling this order as the patient has declined to move forward with testing at this time. Please feel free to re-refer if the patient changes their mind.',
  },
  {
    value: 3,
    label: 'Patient Tested with Another Provider',
    template:
      'We are canceling this order as we have been informed that the patient has completed or scheduled testing with another provider.',
  },
  {
    value: 4,
    label: 'Out of Network',
    template:
      "We are canceling this order as TestSmarter is currently out of network for this patient's particular insurance plan.",
  },
  {
    value: 5,
    label: 'Patient Unable to Afford',
    template:
      'We are canceling this order as the patient has indicated they are unable to cover the out-of-pocket costs associated with testing at this time. Payment plans have been offered but not accepted.',
  },
  {
    value: 7,
    label: 'Duplicate Order',
    template:
      'We are canceling this order as it appears to be a duplicate of an existing order already on file for this patient. Please reference the original order for status updates.',
  },
  {
    value: 8,
    label: 'Test Order',
    template:
      'We are canceling this order as it has been identified as a test submission. No further action is needed.',
  },
  {
    value: 9,
    label: 'Minor',
    template:
      'We are canceling this order as the patient does not meet the age requirement for testing. Home sleep testing is not indicated for patients under 18. Please consult with the ordering provider regarding next steps.',
  },
  {
    value: HST_DISQUALIFIED_CANCELLATION_REASON_VALUE,
    label: 'Patient Disqualified for HST',
    template:
      'We are canceling this order as the patient reported a medical condition that disqualifies them from home sleep testing per screening criteria. The patient will require an in-lab sleep study. A notification has been faxed to the referring provider.',
  },
  {
    value: 11,
    label: 'Patient Deceased',
    template:
      'We are canceling this order as we have been informed that the patient is deceased. No further action is needed.',
  },
  {
    value: 12,
    label: 'Incomplete Referral',
    template:
      'We are canceling this order as the referral is missing information required to proceed with testing. Please feel free to re-refer with the complete documentation.',
  },
  {
    value: OTHER_CANCELLATION_REASON_VALUE,
    label: 'Other'
  }
]

export const isOtherCancellationReason = (
  reason?: CancellationReason | null,
): boolean => reason?.value === OTHER_CANCELLATION_REASON_VALUE

export const isHstDisqualifiedCancellationReason = (
  reason?: CancellationReason | null,
): boolean => reason?.value === HST_DISQUALIFIED_CANCELLATION_REASON_VALUE

export const getCancellationReasonTemplate = (
  reason?: CancellationReason | null,
): string => reason?.template || ''
